<script lang="ts" generics="Datum">
	import { Html } from '../index.js';
	import { mergeAttributes, Synced, watch } from '@vpap_svelte/internal';

	import {
		useGridKeyboard,
		useHierarchicalKeyboard,
		useFlatKeyboard,
		type KeyboardSelectorProps,
	} from './keyboard/index.js';
	import type { HTMLElementProps } from '@vpap_svelte/internal';

	// this mechanism for typing out props looks cludgy, but TypeScript's discriminated unions doesn't work with spread props
	let componentProps: KeyboardSelectorProps<Datum> = $props();

	let controller = $derived.by(() => {
		switch (componentProps.type) {
			case 'flat':
				return useFlatKeyboard({
					data: componentProps.data,
					wrap: componentProps.wrap ?? true,
					skipIf: componentProps.skipIf,
					orientation: componentProps.orientation,
				});
			case 'grid':
				return useGridKeyboard({
					data: componentProps.data,
					wrap: componentProps.wrap ?? true,
					orientation: componentProps.orientation,
				});
			case 'hierarchy':
				return useHierarchicalKeyboard({
					data: componentProps.data,
					wrap: componentProps.wrap ?? true,
					orientation: componentProps.orientation,
					skipIf: componentProps.skipIf,
				});
		}
	});

	// https://github.com/sveltejs/language-tools/issues/1026#issuecomment-1447069823
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const notypecheck = (x: any) => x;

	let children = $derived(componentProps.children);
	let attrs = $derived.by(() => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { data, type, orientation, wrap, onSelectionChange, children, skipIf, ...rest } =
			componentProps;
		return rest;
	});

	type SelectionChangeEvent = NonNullable<typeof componentProps.onSelectionChange>;
	type Selection = Parameters<SelectionChangeEvent>[0];

	let selection = new Synced<Selection>({
		value: undefined,
		// @ts-expect-error this is happening because TypeScript can't handle the discriminated type correctly here
		onChange: componentProps.onSelectionChange,
	});

	let requiredAttrs: HTMLElementProps<HTMLDivElement> = $derived(controller.attributes);

	// technically this type is incorrect. But we have to use  this in order for event chaining
	// to work
	let mergedProps = $derived(
		mergeAttributes({ ...attrs }, { ...requiredAttrs }),
	) as HTMLElementProps<HTMLDivElement>;

	watch(
		() => controller.selection,
		() => {
			selection.current = controller.selection;
		},
	);
</script>

<Html {...mergedProps}>
	{#if children}
		<!-- @ts-expect-error Typescript can't properly handle the discriminated types here -->
		{@render children(notypecheck(controller.selection))}
	{/if}
</Html>
